import { createContext, useContext, useState, useRef, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { revokeAttachmentPreview } from '../utils/attachmentUtils.js';

export const FileAttachmentsContext = createContext(null);

/**
 * Consume the file attachments context. Must be used inside a FileAttachmentsContext.Provider.
 */
export const useFileAttachments = () => {
    const ctx = useContext(FileAttachmentsContext);
    if (!ctx) {
        throw new Error('useFileAttachments must be used within a FileAttachmentsContext.Provider');
    }
    return ctx;
};

/**
 * Creates and manages all file attachment state and operations.
 * The returned object should be passed as the value to FileAttachmentsContext.Provider
 * so that child components can consume it via useFileAttachments().
 *
 * @param {Object} params
 * @param {string} params.conversationId - Optional fixed conversation ID
 * @param {string} params.botId - Bot identifier
 * @param {string} params.orgId - Organisation identifier (may be empty until bot info loads)
 * @param {Function} params.t - i18n translation function
 */
const useFileAttachmentsStore = ({ conversationId, botId, orgId, t }) => {
    const apiKey = process.env.REACT_APP_BOT_API_KEY;
    const mediaApiURL = process.env.REACT_APP_MEDIA_API_URL || 'https://media.brainstormer.dev';

    const [attachments, setAttachments] = useState([]);
    const [isUploadingFiles, setIsUploadingFiles] = useState(false);
    const [fileSharingEnabled, setFileSharingEnabled] = useState(false);
    const [fileSizeLimitBytes, setFileSizeLimitBytes] = useState(null);
    const [allowedFileTypes, setAllowedFileTypes] = useState([]);
    const [attachmentsUploadResponse, setAttachmentsUploadResponse] = useState(null);

    const attachmentsRef = useRef([]);
    const attachmentTriggerRef = useRef(null);

    // Keep ref in sync so the cleanup effect always has the latest list
    useEffect(() => {
        attachmentsRef.current = attachments;
    }, [attachments]);

    // Revoke all preview URLs on unmount
    useEffect(() => {
        return () => {
            attachmentsRef.current.forEach((attachment) => revokeAttachmentPreview(attachment));
        };
    }, []);

    /**
     * Uploads processed attachment objects to the external files API.
     * Updates each attachment's temporary ID with the real server-assigned ID.
     * @param {Array} newAttachments - Array of processed attachment objects
     */
    const uploadAttachmentsToExternalAPI = async (newAttachments = []) => {
        if (!newAttachments.length) return;

        setIsUploadingFiles(true);
        try {
            const chatIDForUpload = conversationId || localStorage.getItem(`chatId_${botId}`);
            const formData = new FormData();

            formData.append('bot_id', `bot_${botId}`);
            if (chatIDForUpload) {
                formData.append('conversation_id', chatIDForUpload);
            }
            if (orgId) {
                formData.append('org_id', orgId);
            }
            newAttachments.forEach((attachment) => {
                if (attachment?.file) {
                    formData.append('files', attachment.file, attachment.name);
                }
            });

            const response = await axios.post(`${mediaApiURL}/files`, formData, {
                headers: {
                    'Api-token': apiKey,
                    'X-HMAC-Key': chatIDForUpload,
                    ...(orgId ? { 'X-ORG-ID': orgId } : {}),
                },
            });

            // Handle both response structures: { data: [...] } or [...]
            const responseFiles = Array.isArray(response?.data?.data)
                ? response.data.data
                : Array.isArray(response?.data)
                ? response.data
                : null;

            if (responseFiles && responseFiles.length > 0) {
                setAttachments((prevAttachments) => {
                    const updatedAttachments = [...prevAttachments];
                    const successfullyUploadedFiles = [];
                    const processedFileIds = new Set();

                    newAttachments.forEach((newAttachment, index) => {
                        if (responseFiles[index]?.id) {
                            const fileId = responseFiles[index].id;

                            if (processedFileIds.has(fileId)) return;

                            const attachmentIndex = updatedAttachments.findIndex(
                                (att) => att.id === newAttachment.id
                            );

                            if (attachmentIndex !== -1) {
                                updatedAttachments[attachmentIndex] = {
                                    ...updatedAttachments[attachmentIndex],
                                    id: fileId,
                                };
                                successfullyUploadedFiles.push(responseFiles[index]);
                                processedFileIds.add(fileId);
                            }
                            // If attachment was deleted during upload, skip it
                        }
                    });

                    if (successfullyUploadedFiles.length > 0) {
                        setAttachmentsUploadResponse((prevResponse) => {
                            const existingFiles = prevResponse?.data
                                ? Array.isArray(prevResponse.data) ? prevResponse.data : []
                                : [];

                            const existingFileIds = new Set(existingFiles.map((f) => f.id).filter(Boolean));
                            const newFilesToAdd = successfullyUploadedFiles.filter(
                                (f) => f.id && !existingFileIds.has(f.id)
                            );

                            if (newFilesToAdd.length === 0) return prevResponse;

                            const updatedFiles = [...existingFiles, ...newFilesToAdd];

                            if (prevResponse && typeof prevResponse === 'object' && !Array.isArray(prevResponse)) {
                                return { ...prevResponse, data: updatedFiles };
                            }
                            return Array.isArray(response?.data)
                                ? { data: updatedFiles }
                                : { ...response?.data, data: updatedFiles };
                        });
                    }

                    return updatedAttachments;
                });
            }
        } catch (error) {
            console.error('Error uploading attachments:', error);
            toast.error(t('File upload failed. Please try again.'), {
                position: 'top-right',
                style: {
                    backgroundColor: 'white',
                    color: 'red',
                    fontSize: '13px',
                    padding: '5px 7px',
                    fontWeight: '500',
                },
            });
        } finally {
            setIsUploadingFiles(false);
        }
    };

    /**
     * Adds already-processed attachment objects to state then uploads them.
     * @param {Array} processedAttachments - Array of hydrated attachment objects
     */
    const handleAttachmentsAdd = async (processedAttachments = []) => {
        if (!processedAttachments.length) return;
        setAttachments((prev) => [...prev, ...processedAttachments]);
        await uploadAttachmentsToExternalAPI(processedAttachments);
    };

    /**
     * Removes a single attachment by ID and cleans up its upload response entry.
     * Note: callers in FileAttachments already call revokeAttachmentPreview before
     * invoking this handler, so we only revoke here for safety when there is no preview URL
     * to double-revoking concerns.
     * @param {string} attachmentId
     */
    const handleAttachmentRemove = (attachmentId) => {
        const toDelete = attachments.find((attachment) => attachment.id === attachmentId);

        if (toDelete) {
            revokeAttachmentPreview(toDelete);
            setAttachments((prev) => prev.filter((attachment) => attachment.id !== attachmentId));

            if (toDelete.id) {
                setAttachmentsUploadResponse((prevResponse) => {
                    if (!prevResponse) return null;

                    const files = prevResponse?.data;
                    if (!Array.isArray(files)) return prevResponse;

                    const updatedFiles = files.filter((file) => file.id !== toDelete.id);
                    if (updatedFiles.length === 0) return null;

                    return { ...prevResponse, data: updatedFiles };
                });
            }
        }
    };

    /**
     * Clears all attachments and their upload response.
     * @param {boolean} shouldRevokePreviews - Whether to revoke object URLs (default true)
     */
    const clearAttachments = (shouldRevokePreviews = true) => {
        setAttachments((prev) => {
            if (shouldRevokePreviews) {
                prev.forEach((item) => revokeAttachmentPreview(item));
            }
            return [];
        });
        setAttachmentsUploadResponse(null);
    };

    /**
     * Registers the file-dialog trigger function exposed by the FileAttachments component.
     * @param {Function|null} handler
     */
    const registerAttachmentTrigger = (handler) => {
        attachmentTriggerRef.current = handler;
    };

    /** Programmatically opens the file-picker dialog. */
    const handleAttachmentTrigger = () => {
        if (attachmentTriggerRef.current) {
            attachmentTriggerRef.current();
        }
    };

    return {
        attachments,
        isUploadingFiles,
        fileSharingEnabled,
        fileSizeLimitBytes,
        allowedFileTypes,
        attachmentsUploadResponse,
        orgId,
        
        setFileSharingEnabled,
        setFileSizeLimitBytes,
        setAllowedFileTypes,

        handleAttachmentsAdd,
        handleAttachmentRemove,
        clearAttachments,
        registerAttachmentTrigger,
        handleAttachmentTrigger,
    };
};

export default useFileAttachmentsStore;
